/**
 * Locale-aware wrappers for date-fns.
 *
 * date-fns formats in English unless given a Locale object; these wrappers
 * inject the locale matching the current i18next language so relative dates
 * ("il y a 5 minutes") follow the UI language. Import from here instead of
 * 'date-fns' for any user-facing distance formatting.
 */

import {
  format as formatBase,
  formatDistanceToNow as formatDistanceToNowBase,
  formatRelative as formatRelativeBase,
  type FormatDistanceToNowOptions,
  type FormatOptions,
  type FormatRelativeOptions,
} from 'date-fns';
import { de, es, fr } from 'date-fns/locale';
import i18n from './index';

import type { Locale } from 'date-fns';

const LOCALES: Record<string, Locale> = { fr, es, de };

function currentLocale(): Locale | undefined {
  const lang = (i18n.language || 'en').slice(0, 2);
  return LOCALES[lang];
}

export function formatDistanceToNow(
  date: Date | number | string,
  options?: FormatDistanceToNowOptions
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = currentLocale();
  return formatDistanceToNowBase(d, locale ? { ...options, locale } : options);
}

/**
 * Locale-aware replacement for date-fns `format`. Month and weekday names
 * follow the active UI language. Import this instead of `format` from
 * 'date-fns' so dates render in the user's language.
 */
export function format(
  date: Date | number | string,
  formatStr: string,
  options?: FormatOptions
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = currentLocale();
  return formatBase(d, formatStr, locale ? { ...options, locale } : options);
}

/**
 * Locale-aware replacement for date-fns `formatRelative`.
 */
export function formatRelative(
  date: Date | number | string,
  baseDate: Date | number = new Date(),
  options?: FormatRelativeOptions
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = currentLocale();
  return formatRelativeBase(d, baseDate, locale ? { ...options, locale } : options);
}
